import React from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {TermEntry} from '../descriptor-types';
import {TERMS_TABLE_CONFIG} from '../descriptor-field-config';
import {ERROR_MESSAGES} from '../descriptor-constants';

interface DescriptorTermsTableProps {
    termEntries: TermEntry[];
    componentId: string;
    isNotEditable: boolean;
    onUpdateTermKey: (event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => void;
    onUpdateTermValue: (event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => void;
    onRemoveTerm: (index: number) => void;
    onAddNewTerm: () => void;
    canAddNewTerm: () => boolean;
    canRemoveTerm: (term: TermEntry) => boolean;
}

/**
 * Component for managing descriptor terms table
 */
export const DescriptorTermsTable: React.FC<DescriptorTermsTableProps> = ({
    termEntries,
    componentId,
    isNotEditable,
    onUpdateTermKey,
    onUpdateTermValue,
    onRemoveTerm,
    onAddNewTerm,
    canAddNewTerm,
    canRemoveTerm
}) => {
    return (
        <>
            <Box sx={{ marginLeft: '5px', marginRight: '10px' }}>
                <Typography variant="h6" component="div">
                    Descriptor Terms
                </Typography>
                <Typography component="div">
                    {TERMS_TABLE_CONFIG.instructions}
                </Typography>
            </Box>
            
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="descriptor terms table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" sx={{ width: 200 }}>Term</TableCell>
                            <TableCell align="left">Definition</TableCell>
                            <TableCell align="center" sx={{ width: 150 }}>
                                <Button
                                    variant='contained'
                                    title='Add New Term'
                                    component='button'
                                    style={{ textTransform: 'none' }}
                                    onClick={onAddNewTerm}
                                    startIcon={<AddIcon />}
                                    disabled={isNotEditable || !canAddNewTerm()}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                >
                                    Add New Term
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {termEntries.map((entry: TermEntry, index: number) => (
                            <TableRow key={index}>
                                <TableCell sx={{ py: 1, width: 200 }}>
                                    <TextField
                                        aria-label={`${entry.key.text || 'Term'} label`}
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`term-${index}-label-input-${componentId}`}
                                        value={`${entry.key.text}`}
                                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                                            onUpdateTermKey(event, index);
                                        }}
                                        placeholder="Enter term"
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                                fontSize: '0.875rem',
                                                padding: '8px 10px',
                                            }
                                        }}
                                        disabled={!canRemoveTerm(entry) || isNotEditable}
                                        error={entry.key.error}
                                        helperText={entry.key.errorText ?? ''}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <TextField
                                        aria-label={`${entry.key.text || 'Term'} value`}
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`term-${index}-value-input-${componentId}`}
                                        value={`${entry.value.text}`}
                                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                                            onUpdateTermValue(event, index);
                                        }}
                                        placeholder="Enter definition"
                                        sx={{
                                            '& textarea': {
                                                border: 'none',
                                                fontSize: '0.875rem',
                                                resize: 'vertical',
                                            }
                                        }}
                                        slotProps={{
                                            input: {
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `term-${index}-input-${componentId}`,
                                                'aria-label': `${entry.key.text || 'Term definition'}`
                                            }
                                        }}
                                        disabled={isNotEditable}
                                        error={entry.value.error}
                                        helperText={entry.value.error ? ERROR_MESSAGES.TERM_VALUE_REQUIRED(entry.key.text!) : ''}
                                        multiline
                                        minRows={2}
                                        maxRows={4}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ width: 150, py: 1 }}>
                                    {!canRemoveTerm(entry) ? '' :
                                        <Button 
                                            variant="contained" 
                                            color="error"
                                            onClick={(): void => {
                                                onRemoveTerm(index);
                                            }}
                                            disabled={isNotEditable}
                                            startIcon={<DeleteIcon />}
                                            title='Remove Term'
                                            aria-label="Remove Term"
                                            size="small"
                                            sx={{ minWidth: 120 }}
                                        >
                                            Remove
                                        </Button>
                                    }
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};